import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { BalanceSheetResponse, BalanceSheetQueryParams } from "../types";

interface UseBalanceSheetResult {
  report: BalanceSheetResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch Balance Sheet report.
 *
 * Returns null if fiscalYearId is not provided.
 */
export function useBalanceSheet(
  params: Partial<BalanceSheetQueryParams>
): UseBalanceSheetResult {
  const [report, setReport] = useState<BalanceSheetResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  const { fiscalYearId, asOfDate, includeZeroBalances } = params;

  useEffect(() => {
    // Don't fetch if fiscalYearId is not provided
    if (!fiscalYearId) {
      setReport(null);
      setIsLoading(false);
      return;
    }

    const fetchReport = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const queryParams = new URLSearchParams();
        queryParams.set("fiscalYearId", fiscalYearId);
        if (asOfDate) {
          queryParams.set("asOfDate", asOfDate);
        }
        if (includeZeroBalances) {
          queryParams.set("includeZeroBalances", "true");
        }

        const url = `/api/reports/balance-sheet?${queryParams.toString()}`;
        const data = await apiFetch.get<BalanceSheetResponse>(url);
        setReport(data);
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err);
        } else {
          setError(err instanceof Error ? err : new Error("Unknown error"));
        }
        setReport(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReport();
  }, [fetchKey, fiscalYearId, asOfDate, includeZeroBalances]);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  return {
    report,
    isLoading,
    error,
    refetch,
  };
}
